/**
 * Utility functions for formatting data
 */

/**
 * Format currency in TRY (Turkish Lira)
 * @param amount Amount in TRY (not cents)
 * @returns Formatted string like "50₺"
 */
export function formatCurrency(amount: number | string | null | undefined): string {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount) || 0;
  if (isNaN(numericAmount)) {
    return '0₺';
  }
  return `${numericAmount.toFixed(0)}₺`;
}

/**
 * Safely parse and format rating value
 * Backend may return rating as string (from PostgreSQL DECIMAL)
 * @param rating Rating value (number or string)
 * @param decimals Number of decimal places (default: 1)
 * @returns Formatted rating string
 */
export function formatRating(rating: number | string, decimals: number = 1): string {
  const numericRating = typeof rating === 'string' ? parseFloat(rating) : rating;
  
  if (isNaN(numericRating)) {
    return '0.0';
  }
  
  return numericRating.toFixed(decimals);
}

/**
 * Normalize Turkish mobile number to E.164 (+905XXXXXXXXX).
 */
export function normalizeTurkishMobileToE164(phone: string): string {
  const trimmed = phone.trim();
  const digits = trimmed.replace(/\D/g, '');

  if (!digits) {
    return '';
  }

  if (trimmed.startsWith('+')) {
    return `+${digits}`;
  }

  if (digits.startsWith('90') && digits.length === 12) {
    return `+${digits}`;
  }

  if (digits.startsWith('0') && digits.length === 11) {
    return `+90${digits.slice(1)}`;
  }

  if (digits.length === 10 && digits.startsWith('5')) {
    return `+90${digits}`;
  }

  if (digits.startsWith('90')) {
    return `+${digits}`;
  }

  if (digits.startsWith('0')) {
    return `+90${digits.slice(1)}`;
  }

  return `+90${digits}`;
}

/**
 * Validates Turkish mobile numbers (+905XXXXXXXXX — 10 digits after country code).
 */
export function isValidTurkishMobilePhone(phone: string): boolean {
  const normalized = normalizeTurkishMobileToE164(phone);
  return /^\+905\d{9}$/.test(normalized);
}

/**
 * Display as local 05XXXXXXXXX when possible.
 */
export function formatTurkishMobileForDisplay(phone: string): string {
  const normalized = normalizeTurkishMobileToE164(phone);
  if (/^\+905\d{9}$/.test(normalized)) {
    return `0${normalized.slice(3)}`;
  }

  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('0') && digits.length === 11) {
    return digits;
  }
  if (digits.length === 10 && digits.startsWith('5')) {
    return `0${digits}`;
  }

  return phone.trim();
}

export function getTurkishMobileValidationError(phone: string): string | null {
  const trimmed = phone.trim();
  if (!trimmed) {
    return 'Telefon numarası gereklidir.';
  }

  const normalized = normalizeTurkishMobileToE164(trimmed);
  if (/^\+905\d{9}$/.test(normalized)) {
    return null;
  }

  const digits = trimmed.replace(/\D/g, '');
  let subscriberLength = digits.length;

  if (digits.startsWith('90') && digits.length > 2) {
    subscriberLength = digits.length - 2;
  } else if (digits.startsWith('0') && digits.length > 1) {
    subscriberLength = digits.length - 1;
  }

  if (subscriberLength < 10) {
    return `Telefon numarası eksik (${subscriberLength}/10 hane). Örnek: 05551234567`;
  }

  if (subscriberLength > 10) {
    return 'Telefon numarası çok uzun. Örnek: 05551234567';
  }

  if (!digits.startsWith('0') && !digits.startsWith('90') && !digits.startsWith('5') && !trimmed.startsWith('+')) {
    return 'Geçerli bir Türkiye cep telefonu girin. Numara 05 ile başlamalıdır.';
  }

  return 'Geçerli bir Türkiye cep telefonu girin (örn. 05551234567).';
}

/**
 * Format phone number to E.164 format
 * @param phone Phone number (with or without country code)
 * @returns E.164 formatted phone (e.g., "+905551234567")
 */
export function formatPhoneE164(phone: string): string {
  const normalized = normalizeTurkishMobileToE164(phone);
  if (normalized) {
    return normalized;
  }

  if (phone.startsWith('+')) {
    const cleaned = phone.replace(/[^\d+]/g, '');
    if (/^\+[1-9]\d{1,14}$/.test(cleaned)) {
      return cleaned;
    }
  }

  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('90')) {
    return `+${digits}`;
  }
  if (digits.startsWith('0')) {
    return `+90${digits.substring(1)}`;
  }
  if (digits.startsWith('5') && digits.length === 10) {
    return `+90${digits}`;
  }
  if (digits.length === 10) {
    return `+90${digits}`;
  }
  return `+${digits}`;
}

/**
 * Format time duration in minutes
 * @param minutes Duration in minutes
 * @returns Formatted string like "25 dk" or "1 saat"
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} dk`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours} saat`;
  }
  
  return `${hours}s ${remainingMinutes}dk`;
}

/**
 * Format price in Turkish Lira
 * @param amount Price in Turkish Lira (e.g., 57.37 = 57 TL 37 Kuruş)
 * @returns Formatted price string
 */
export function formatPrice(amount: number | string | null | undefined): string {
  return formatCurrency(amount);
}
